import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiSearch, IoIosArrowDown, IoIosArrowUp } from "../assets/icons/vander";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../utils/axiosInstance";
import { handleGetLanguage } from "../utils/languageStorage";
import { useMediaQuery } from "usehooks-ts";

/* -------------------------------
   Range Selector
---------------------------------*/
const RangeSelector = ({ onSelect, language }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [from, setFrom] = useState("0");
  const [to, setTo] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  const removeQuery = (key) => {
    const params = new URLSearchParams(location.search);
    params.delete(key);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    if (!isVisible) return;
    const T = setTimeout(() => {
      if ((!from || from === "0") && (!to || to === "0")) {
        removeQuery("price");
      } else {
        navigate(onSelect("price", `${from},${to}`));
      }
    }, 500);
    return () => clearTimeout(T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, isVisible]);

  return (
    <div className="border-t-2 pt-4 mt-4">
      <button
        className="font-medium cursor-pointer w-full flex justify-between text-lg"
        onClick={() => setIsVisible(!isVisible)}
      >
        {language === "en" ? "Price Range" : "ფასი"}
        {isVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
      </button>

      {isVisible && (
        <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
          <input
            type="text"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder={language === "en" ? "From" : "დან"}
            className="w-full sm:w-[48%] p-2 border rounded dark:bg-transparent dark:border-gray-700"
          />
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={language === "en" ? "To" : "მდე"}
            className="w-full sm:w-[48%] p-2 border rounded dark:bg-transparent dark:border-gray-700"
          />
        </div>
      )}
    </div>
  );
};

/* =============================================
   MAIN FILTER COMPONENT
============================================= */
export default function Filter({
  genderQuery,
  brandQuery,
  sizeQuery,
  colorQuery,
  categorieQuery,
  removeQuery,
  updateQuery,
  productsCount,
  onShowClick,
  loading,
  showCount,
}) {
  const language = handleGetLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  /* UI states (all closed by default) */
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isGenderVisible, setIsGenderVisible] = useState(false);
  const [isCategoriesVisible, setIsCategoriesVisible] = useState(false);
  const [isBrandsVisible, setIsBrandsVisible] = useState(false);
  const [isSizesVisible, setIsSizesVisible] = useState(false);
  const [isColorsVisible, setIsColorsVisible] = useState(false);

  /* search inputs */
  const [brandSearch, setBrandSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [sizeSearch, setSizeSearch] = useState("");

  /* fetch filters */
  const { data } = useQuery({
    queryKey: ["ProductFilters"],
    queryFn: async () => {
      const r = await axiosInstance.get("/products/filters");
      return r.data;
    },
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const r = await axiosInstance.get("/categories");
      return r.data;
    },
  });

  /* helpers */
  const safeLower = (v) => (typeof v === "string" ? v.toLowerCase() : "");

  /* parse multi-select params (comma lists) */
  const selectedGenders = genderQuery ? genderQuery.split(",").map(Number) : [];
  const selectedBrands = brandQuery ? brandQuery.split(",").map(Number) : [];
  const selectedCategories = categorieQuery ? categorieQuery.split(",").map(Number) : [];
  const selectedSizes = sizeQuery ? sizeQuery.split(",").map(Number) : [];
  const selectedColors = colorQuery ? colorQuery.split(",").map(Number) : [];

  /* selected chips */
  const selectedChips = [];

  selectedGenders.forEach((id) => {
    selectedChips.push({
      key: "gender",
      value: id,
      label:
        id === 1
          ? language === "en"
            ? "Women"
            : "ქალი"
          : id === 2
          ? language === "en"
            ? "Men"
            : "კაცი"
          : language === "en"
          ? "Kids"
          : "ბავშვი",
    });
  });

  selectedCategories.forEach((id) => {
    const cat = categoriesData?.find?.((c) => c?.id === id);
    if (cat)
      selectedChips.push({
        key: "categories",
        value: id,
        label: cat?.name?.[language] ?? "",
      });
  });

  selectedBrands.forEach((id) => {
    const b = data?.brands?.find?.((x) => x?.id === id);
    if (b)
      selectedChips.push({
        key: "brands",
        value: id,
        label: b?.name?.[language] ?? "",
      });
  });

  selectedSizes.forEach((id) => {
    const s = data?.sizes?.find?.((x) => x?.id === id);
    if (s)
      selectedChips.push({
        key: "sizes",
        value: id,
        label: s?.name ?? "",
      });
  });

  selectedColors.forEach((id) => {
    const c = data?.colors?.find?.((x) => x?.id === id);
    if (c)
      selectedChips.push({
        key: "colors",
        value: id,
        label: c?.name?.[language] ?? "",
      });
  });

  /* filtered lists (with safe label access) */
  const filteredBrands = data?.brands?.filter?.((b) => {
    const label = b?.name?.[language];
    return label && safeLower(label).includes(safeLower(brandSearch));
  });

  const filteredCategories = categoriesData?.filter?.((c) => {
    const label = c?.name?.[language];
    return label && safeLower(label).includes(safeLower(categorySearch));
  });

  const filteredSizes = data?.sizes?.filter?.((s) =>
    safeLower(s?.name ?? "").includes(safeLower(sizeSearch))
  );

  /* toggle helper for any multi-select param (including gender now) */
  const toggleMultiSelect = (key, queryList, id) => {
    const newList = queryList.includes(id)
      ? queryList.filter((x) => x !== id)
      : [...queryList, id];

    if (newList.length === 0) {
      navigate(removeQuery(key));
    } else {
      navigate(updateQuery(key, newList.join(",")));
    }
  };

  /* clear ALL params (keep path) */
  const handleClearAll = () => {
    navigate(location.pathname);
  };

  /* remove a single chip (do NOT wipe others) */
  const handleRemoveChip = (chip) => {
    const params = new URLSearchParams(location.search);
    const key = chip.key;
    const valueStr = String(chip.value);
    const currentVal = params.get(key);

    if (!currentVal) return;

    const updated = currentVal.split(",").filter((v) => v !== valueStr);
    if (updated.length === 0) params.delete(key);
    else params.set(key, updated.join(","));

    navigate(`${location.pathname}?${params.toString()}`);
  };

  /* lock scroll when mobile panel is open */
  useEffect(() => {
    document.body.style.overflow = isFilterVisible ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isFilterVisible]);

  return (
    <>
<button
  className={`fixed top-[30%] right-[-22px] -rotate-90 z-[99] md:hidden
              text-left p-2 rounded-lg border bg-orange-500 text-white dark:bg-gray-700
              whitespace-nowrap flex items-center gap-1 transition-all duration-300
              ${isFilterVisible ? "translate-x-[calc(-100vw+43px)]" : "translate-x-0"}`}
  onClick={() => setIsFilterVisible((v) => !v)}
>
  {language === "en" ? "Filter" : "ფილტრი"}
  <span className="text-[10px] ml-1">{isFilterVisible ? "▼" : "▲"}</span>
</button>


      {/* Backdrop for mobile */}
      {isFilterVisible && (
        <div
          className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-40 md:hidden"
          onClick={() => setIsFilterVisible(false)}
        />
      )}

      {/* Slide-in panel (mobile) / sticky (desktop) */}
      <div
        className={`fixed md:sticky top-[80px] right-0 md:left-0
                    z-50 md:z-40 bg-white dark:bg-gray-900 shadow-md
                    w-[calc(100vw-40px)] md:w-1/4
                    max-h-[80vh] md:max-h-[calc(100vh-100px)]
                    overflow-y-auto p-5
                    transform transition-transform duration-300
                    ${isFilterVisible ? "translate-x-0" : "translate-x-full md:translate-x-0"}`}
      >
        {/* Header + Clear all */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold">
            {language === "en" ? "Filter" : "ფილტრი"}
          </h2>
          {selectedChips.length > 0 && (
            <button
              onClick={handleClearAll}
              className="text-sm text-red-500 underline"
            >
              {language === "en" ? "Clear all" : "გასუფთავება"}
            </button>
          )}
        </div>

        {/* Selected chips */}
        {selectedChips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedChips.map((chip) => (
              <div
                key={`${chip.key}-${chip.value}`}
                className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full flex items-center gap-2 text-sm"
              >
                <span>{chip.label}</span>
                <button
                  onClick={() => handleRemoveChip(chip)}
                  className="text-red-500 font-bold text-xs"
                  aria-label="Remove filter"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Gender (now multi-select) */}
        <div className="border-t-2 pt-4">
          <button
            className="flex justify-between w-full text-lg font-medium"
            onClick={() => setIsGenderVisible((v) => !v)}
          >
            {language === "en" ? "Genders" : "სქესი"}
            {isGenderVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          {isGenderVisible && (
            <div className="mt-4 space-y-3">
              {[1, 2, 3].map((id) => (
                <button
                  key={id}
                  className="flex items-center gap-2"
                  onClick={() => toggleMultiSelect("gender", selectedGenders, id)}
                >
                  <div
                    className={`h-4 w-4 border ${
                      selectedGenders.includes(id) ? "bg-orange-500" : ""
                    }`}
                  />
                  <span>
                    {id === 1
                      ? language === "ka"
                        ? "ქალი"
                        : "Women"
                      : id === 2
                      ? language === "ka"
                        ? "კაცი"
                        : "Men"
                      : language === "ka"
                      ? "ბავშვი"
                      : "Kids"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="border-t-2 pt-4 mt-4">
          <button
            className="flex justify-between w-full text-lg font-medium"
            onClick={() => setIsCategoriesVisible((v) => !v)}
          >
            {language === "en" ? "Categories" : "კატეგორია"}
            {isCategoriesVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          {isCategoriesVisible && (
            <div className="mt-4 space-y-3">
              <input
                type="text"
                placeholder={language === "en" ? "Search..." : "ძიება..."}
                className="w-full p-2 border rounded dark:bg-transparent dark:border-gray-700"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
              {filteredCategories?.map?.((item) => {
                const id = item?.id;
                const label = item?.name?.[language] ?? "";
                const isSelected = selectedCategories.includes(id);
                return (
                  <button
                    key={id}
                    className="flex items-center gap-2"
                    onClick={() => toggleMultiSelect("categories", selectedCategories, id)}
                  >
                    <div
                      className={`h-4 w-4 border ${
                        isSelected ? "bg-orange-500" : ""
                      }`}
                    />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Brands */}
        <div className="border-t-2 pt-4 mt-4">
          <button
            className="flex justify-between w-full text-lg font-medium"
            onClick={() => setIsBrandsVisible((v) => !v)}
          >
            {language === "en" ? "Brands" : "ბრენდები"}
            {isBrandsVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          {isBrandsVisible && (
            <div className="mt-4 space-y-3">
              <input
                type="text"
                placeholder={
                  language === "en" ? "Search brand..." : "ბრენდის ძიება..."
                }
                className="w-full p-2 border rounded dark:bg-transparent dark:border-gray-700"
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
              />
              {filteredBrands?.map?.((item) => {
                const id = item?.id;
                const label = item?.name?.[language] ?? "";
                const isSelected = selectedBrands.includes(id);
                return (
                  <button
                    key={id}
                    className="flex items-center gap-2"
                    onClick={() => toggleMultiSelect("brands", selectedBrands, id)}
                  >
                    <div
                      className={`h-4 w-4 border ${
                        isSelected ? "bg-orange-500" : ""
                      }`}
                    />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Colors */}
        <div className="border-t-2 pt-4 mt-4">
          <button
            className="flex justify-between w-full text-lg font-medium"
            onClick={() => setIsColorsVisible((v) => !v)}
          >
            {language === "en" ? "Colors" : "ფერები"}
            {isColorsVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          {isColorsVisible && (
            <div className="mt-4 flex flex-wrap gap-3">
              {data?.colors?.map?.((item) => {
                const id = item?.id;
                const label = item?.name?.[language] ?? "";
                const hex = item?.color_hex || "#ffffff";
                const isSelected = selectedColors.includes(id);

                return (
                  <button
                    key={id}
                    className="relative h-7 w-7 rounded-full border shadow"
                    onClick={() => toggleMultiSelect("colors", selectedColors, id)}
                    style={{ backgroundColor: hex }}
                    title={label}
                  >
                    {isSelected && (
                      <span className="absolute -top-2 -right-2 text-xs bg-orange-500 text-white rounded-full px-1">
                        ✔
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Sizes */}
        <div className="border-t-2 pt-4 mt-4">
          <button
            className="flex justify-between w-full text-lg font-medium"
            onClick={() => setIsSizesVisible((v) => !v)}
          >
            {language === "en" ? "Sizes" : "ზომები"}
            {isSizesVisible ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

        {isSizesVisible && (
          <div className="mt-4 space-y-3">
            <input
              type="text"
              placeholder={
                language === "en" ? "Search size..." : "ზომის ძიება..."
              }
              className="w-full p-2 border rounded dark:bg-transparent dark:border-gray-700"
              value={sizeSearch}
              onChange={(e) => setSizeSearch(e.target.value)}
            />
            {filteredSizes?.map?.((item) => {
              const id = item?.id;
              const label = item?.name ?? "";
              const isSelected = selectedSizes.includes(id);
              return (
                <button
                  key={id}
                  className="flex items-center gap-2"
                  onClick={() => toggleMultiSelect("sizes", selectedSizes, id)}
                >
                  <div
                    className={`h-4 w-4 border ${
                      isSelected ? "bg-orange-500" : ""
                    }`}
                  />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        )}
        </div>

        {/* Price Range */}
        <RangeSelector language={language} onSelect={updateQuery} />

        {/* Sticky Show button (always visible inside panel on mobile) */}
        <div className="sticky bottom-0 left-0 right-0 pt-3 mt-4 bg-white dark:bg-gray-900 border-t dark:border-gray-800">
          <button
            disabled={loading}
            onClick={() => {
              onShowClick();
              if (isMobile) setIsFilterVisible(false);
            }}
            className={`py-3 w-full text-white rounded-lg ${
              loading ? "bg-orange-300" : "bg-orange-500"
            }`}
          >
            {language === "en" ? "Show" : "ჩვენება"}{" "}
            {showCount && <span className="ml-1">({productsCount})</span>}
          </button>
        </div>
      </div>
    </>
  );
}
